import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../config/axios";
import TradePaymentDetailsView from "./TradeDetailsModal/TradePaymentDetailsView";
import {
  buildProgressReportTradePickOptions,
  progressReportTradeLabel,
} from "../utils/progressReportUtils";

const tradeSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "0.875rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 90 }),
  menuPortal: (base) => ({ ...base, zIndex: 90 }),
};

const ProgressReportCommissionDetailsModal = ({
  isOpen,
  onClose,
  trades,
}) => {
  const [selectedTradeId, setSelectedTradeId] = useState("");
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(false);

  const tradeOptions = useMemo(
    () => buildProgressReportTradePickOptions(trades),
    [trades],
  );

  const selectedOption = useMemo(
    () => tradeOptions.find((o) => o.value === selectedTradeId) || null,
    [tradeOptions, selectedTradeId],
  );

  const loadTrade = useCallback(
    async (tradeId) => {
      if (!tradeId) {
        setTrade(null);
        return;
      }
      const cached = (trades || []).find(
        (t) => String(t._id) === String(tradeId),
      );
      if (cached) {
        setTrade(cached);
        return;
      }
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/trades/${tradeId}`);
        setTrade(res.data);
      } catch (e) {
        console.error(e);
        setTrade(null);
      } finally {
        setLoading(false);
      }
    },
    [trades],
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedTradeId("");
      setTrade(null);
      return;
    }
    loadTrade(selectedTradeId);
  }, [isOpen, selectedTradeId, loadTrade]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-details-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-[min(96vw,1100px)] max-h-[min(92vh,900px)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2
              id="commission-details-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Commission Details
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Select a trade to view trust payments, commission trust activity,
              reversals, recalls, and transfers.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="px-5 py-4 border-b border-gray-100 shrink-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Trade
          </label>
          <Select
            inputId="commission-details-trade-select"
            options={tradeOptions}
            value={selectedOption}
            onChange={(opt) => setSelectedTradeId(opt?.value || "")}
            isClearable
            isSearchable
            placeholder="Search by trade # or address…"
            noOptionsMessage={() => "No matching trades"}
            styles={tradeSelectStyles}
            classNamePrefix="commission-details-trade"
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
          />
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 min-h-0">
          {!selectedTradeId ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Select a trade to view payment details.
            </p>
          ) : loading ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Loading trade details…
            </p>
          ) : !trade ? (
            <p className="text-sm text-red-600 py-12 text-center">
              Could not load trade details.
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-900 mb-4">
                {progressReportTradeLabel(trade)}
              </p>
              <TradePaymentDetailsView trade={trade} />
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 shrink-0 flex justify-end bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressReportCommissionDetailsModal;
